<template>
  <aside class="callout-block" :class="`variant-${block.variant || 'info'}`">
    <div class="callout-stripe" aria-hidden="true" />
    <div class="callout-body">
      <div class="callout-header">
        <span class="callout-icon-wrap">
          <q-icon :name="config.icon" size="16px" />
        </span>
        <span class="callout-title">{{ displayTitle }}</span>
      </div>
      <div class="callout-content" v-html="renderedContent" />
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { renderMarkdown } from '../markdown';

interface CalloutBlock {
  type: 'callout';
  variant: 'info' | 'warning' | 'tip' | 'example' | 'key';
  title?: string;
  text?: string;
  content?: string;
}

const props = defineProps<{ block: CalloutBlock }>();

const VARIANT_CONFIG: Record<string, { icon: string; label: string }> = {
  tip: { icon: 'lightbulb', label: 'Consejo' },
  warning: { icon: 'warning', label: 'Atencion' },
  key: { icon: 'star', label: 'Concepto clave' },
  example: { icon: 'menu_book', label: 'Ejemplo' },
  info: { icon: 'info', label: 'Informacion' },
};

const config = computed(() => VARIANT_CONFIG[props.block.variant] || VARIANT_CONFIG.info);
const displayTitle = computed(() => props.block.title || config.value.label);

const renderedContent = computed(() => {
  const text = props.block.text || props.block.content || '';
  return renderMarkdown(text);
});
</script>

<style scoped lang="scss">
.callout-block {
  display: flex;
  align-items: stretch;
  margin: 24px 0;
  border-radius: var(--oa-radius-lg);
  overflow: hidden;
  border: 1px solid var(--callout-border);
  background: var(--callout-bg);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.callout-stripe {
  width: 4px;
  flex-shrink: 0;
  background: var(--callout-color);
}

.callout-body {
  flex: 1;
  min-width: 0;
  padding: 16px 18px;
}

.callout-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.callout-icon-wrap {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--callout-color) 16%, transparent);
  color: var(--callout-color);
  flex-shrink: 0;
}

.callout-title {
  font-weight: 700;
  font-size: var(--oa-text-sm);
  letter-spacing: 0.02em;
  color: var(--callout-color);
  text-transform: uppercase;
}

.callout-content {
  font-size: var(--oa-text-base);
  color: var(--oa-text-primary);
  line-height: 1.65;

  :deep(p) {
    margin: 0 0 10px 0;
    &:last-child { margin-bottom: 0; }
  }

  :deep(a) {
    color: var(--callout-color);
    text-decoration: underline;
  }

  :deep(strong) {
    color: var(--oa-text-primary);
    font-weight: 700;
  }

  :deep(em) { font-style: italic; }

  :deep(code) {
    background: color-mix(in srgb, var(--callout-color) 10%, var(--oa-bg-3));
    padding: 1px 5px;
    border-radius: var(--oa-radius-sm);
    font-family: var(--oa-font-mono);
    font-size: 0.875em;
  }

  :deep(ul), :deep(ol) {
    margin: 8px 0;
    padding-left: 22px;
  }

  :deep(li) {
    margin-bottom: 4px;
  }

  :deep(li::marker) {
    color: var(--callout-color);
  }
}

.variant-info {
  --callout-color: #5a8cc8;
  --callout-border: color-mix(in srgb, #5a8cc8 25%, transparent);
  --callout-bg: color-mix(in srgb, #5a8cc8 5%, var(--oa-bg-2));
}

.variant-tip {
  --callout-color: var(--oa-success);
  --callout-border: color-mix(in srgb, var(--oa-success) 25%, transparent);
  --callout-bg: color-mix(in srgb, var(--oa-success) 5%, var(--oa-bg-2));
}

.variant-warning {
  --callout-color: #d4a017;
  --callout-border: color-mix(in srgb, #d4a017 30%, transparent);
  --callout-bg: color-mix(in srgb, #d4a017 6%, var(--oa-bg-2));
}

.variant-example {
  --callout-color: #5a8cc8;
  --callout-border: color-mix(in srgb, #5a8cc8 22%, transparent);
  --callout-bg: color-mix(in srgb, #5a8cc8 4%, var(--oa-bg-2));
}

.variant-key {
  --callout-color: var(--oa-gold-full);
  --callout-border: color-mix(in srgb, var(--oa-gold-full) 35%, transparent);
  --callout-bg: linear-gradient(
    135deg,
    color-mix(in srgb, var(--oa-gold-full) 8%, var(--oa-bg-2)) 0%,
    color-mix(in srgb, var(--oa-gold-full) 4%, var(--oa-bg-2)) 100%
  );
}
</style>
